import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { QUERY_STALE_TIMES } from '@/lib/cacheManager';
import { differenceInCalendarDays } from 'date-fns';

export interface Subscription {
  id: string;
  restaurant_id: string;
  status: string;
  plan_type: 'basic' | 'premium';
  trial_start_date: string | null;
  trial_end_date: string | null;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
  payment_method: string | null;
  asaas_customer_id: string | null;
  asaas_subscription_id: string | null;
  monthly_amount: number | null;
  created_at: string | null;
  updated_at: string | null;
  approval_status: 'pending' | 'approved' | 'rejected';
  first_login_after_approval: boolean | null;
  canceled_at: string | null;
  cancellation_reason: string | null;
  cancellation_type: string | null;
  rejection_category: string | null;
  rejection_reason: string | null;
}

// Store count result interface
interface StoreCountResult {
  subscription: Subscription | null;
  totalStores: number;
}

export const useSubscription = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription'],
    queryFn: async (): Promise<StoreCountResult> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { subscription: null, totalStores: 0 };

      // Try to get restaurant where user is owner
      let restaurantId: string | null = null;
      
      // Buscar restaurante do dono - incluir todos os status relevantes
      const { data: ownedRestaurant } = await supabase
        .from('restaurants')
        .select('id')
        .eq('user_id', user.id)
        .in('status', ['active', 'pending_approval', 'waiting_payment', 'rejected'])
        .order('is_headquarters', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (ownedRestaurant) {
        restaurantId = ownedRestaurant.id;
      } else {
        // Unit Manager: CO-TERMING defense - fetch HQ subscription instead of branch
        const { data: membership } = await supabase
          .from('restaurant_members')
          .select('restaurant_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (membership?.restaurant_id) {
          // Find the owner of the restaurant I'm a member of
          const { data: myRestaurant } = await supabase
            .from('restaurants')
            .select('user_id')
            .eq('id', membership.restaurant_id)
            .single();

          if (myRestaurant?.user_id) {
            // Find the HQ of the owner to get the "source of truth" subscription
            const { data: ownerHQ } = await supabase
              .from('restaurants')
              .select('id')
              .eq('user_id', myRestaurant.user_id)
              .eq('is_headquarters', true)
              .maybeSingle();

            restaurantId = ownerHQ?.id || membership.restaurant_id;
          } else {
            restaurantId = membership.restaurant_id;
          }
        }
      }

      if (!restaurantId) return { subscription: null, totalStores: 0 };

      // Count ALL stores owned by this user (for multi-store billing)
      const { count: storeCount } = await supabase
        .from('restaurants')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .in('status', ['active', 'waiting_payment']);

      // Get subscription from database
      const { data: subData, error: subError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .maybeSingle();

      if (subError) {
        console.error('Error fetching subscription:', subError);
        return { subscription: null, totalStores: storeCount || 1 };
      }

      if (!subData) return { subscription: null, totalStores: storeCount || 1 };

      const sub = subData as Subscription;

      // If subscription has ended and there's a payment pending, check status
      if (sub.subscription_end_date) {
        const endDate = new Date(sub.subscription_end_date);
        const now = new Date();

        if (endDate < now && sub.status !== 'expired') {
          // Subscription has expired, verify with Asaas if there's a new payment
          try {
            const { data: paymentStatus } = await supabase.functions.invoke('check-payment-status', {
              body: {}
            });

            if (paymentStatus?.status === 'CONFIRMED') {
              // Refetch to get updated subscription
              const { data: updatedSub } = await supabase
                .from('subscriptions')
                .select('*')
                .eq('restaurant_id', restaurantId)
                .single();

              if (updatedSub) {
                return { subscription: updatedSub as Subscription, totalStores: storeCount || 1 };
              }
            }
          } catch (error) {
            console.error('Error checking payment status:', error);
          }
        }
      }

      return { subscription: sub, totalStores: storeCount || 1 };
    },
    refetchOnWindowFocus: false,
    staleTime: QUERY_STALE_TIMES['subscription'],
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const subscription = data?.subscription || null;
  const totalStores = data?.totalStores || 1;

  // Calculate trial days remaining
  const getTrialDaysRemaining = () => {
    if (!subscription?.trial_end_date) return 0;
    const endDate = new Date(subscription.trial_end_date);
    const now = new Date();
    const diffDays = differenceInCalendarDays(endDate, now);
    return Math.max(0, diffDays);
  };

  // Check if trial is expired
  const isTrialExpired = () => {
    if (!subscription?.trial_end_date) return false;
    return new Date(subscription.trial_end_date) < new Date();
  };

  // Check if subscription is active (paid and not expired)
  const isSubscriptionActive = () => {
    if (subscription?.status !== 'active') return false;
    
    // Check if subscription has an end date and if it's still valid
    if (subscription.subscription_end_date) {
      const endDate = new Date(subscription.subscription_end_date);
      const now = new Date();
      return endDate > now;
    }
    
    return true;
  };

  // Check if in trial
  const isInTrial = () => {
    return subscription?.status === 'trial' && !isTrialExpired();
  };

  // Check if pending approval
  const isPendingApproval = () => {
    return subscription?.status === 'pending_approval' || subscription?.approval_status === 'pending';
  };

  // Check if rejected
  const isRejected = () => {
    return subscription?.approval_status === 'rejected';
  };

  // Check if expired (trial ended without subscription) - ONLY for approved users
  const isExpired = () => {
    // If not approved yet, not expired
    if (subscription?.approval_status !== 'approved') return false;
    // If subscription is active, not expired
    if (isSubscriptionActive()) return false;
    // If explicitly marked as expired
    if (subscription?.status === 'expired') return true;
    // If subscription end date passed
    if (subscription?.subscription_end_date) {
      const endDate = new Date(subscription.subscription_end_date);
      if (endDate < new Date()) return true;
    }
    // If trial ended
    return isTrialExpired();
  };

  // Check if user has AI access (premium trial or premium active)
  const hasAIAccess = () => {
    // Trial grants AI access ONLY if the user chose the Pro Chef plan
    if (isInTrial() && subscription?.plan_type === 'premium') return true;
    // Active subscription only has AI if it's premium
    if (isSubscriptionActive() && subscription?.plan_type === 'premium') return true;
    return false;
  };

  // Check if account is suspended
  const isSuspended = () => {
    return subscription?.status === 'suspended';
  };

  // Check if cancellation is scheduled (grace period)
  const isCancellationScheduled = () => {
    if (!subscription?.canceled_at) return false;
    // Has cancellation date AND still active
    return subscription.canceled_at !== null && subscription.status === 'active';
  };

  // Get plan name for display
  const getPlanName = () => {
    if (isPendingApproval()) return 'Aguardando Aprovação';
    if (isRejected()) return 'Acesso Negado';
    if (isInTrial()) return subscription?.plan_type === 'premium' ? 'Pro Chef (Trial)' : 'Essencial (Trial)';
    if (isExpired()) return 'Expirado';
    if (!isSubscriptionActive()) return 'Sem assinatura';
    return subscription?.plan_type === 'premium' ? 'Pro Chef IA' : 'Essencial';
  };

  // Check if user can access dashboard
  const canAccessDashboard = () => {
    // Must be approved
    if (subscription?.approval_status !== 'approved') return false;
    // Must be in trial or active subscription
    return isInTrial() || isSubscriptionActive();
  };

  // Get days remaining in subscription
  const getSubscriptionDaysRemaining = () => {
    if (!subscription?.subscription_end_date) return 0;
    const endDate = new Date(subscription.subscription_end_date);
    const now = new Date();
    const diffDays = differenceInCalendarDays(endDate, now);
    return Math.max(0, diffDays);
  };

  return {
    subscription,
    isLoading,
    error,
    isError: !!error,
    refetch,
    trialDaysRemaining: getTrialDaysRemaining(),
    subscriptionDaysRemaining: getSubscriptionDaysRemaining(),
    isTrialExpired: isTrialExpired(),
    isSubscriptionActive: isSubscriptionActive(),
    isInTrial: isInTrial(),
    isPendingApproval: isPendingApproval(),
    isRejected: isRejected(),
    isExpired: isExpired(),
    hasAIAccess: hasAIAccess(),
    canAccessDashboard: canAccessDashboard(),
    planType: subscription?.plan_type || 'premium',
    planName: getPlanName(),
    approvalStatus: subscription?.approval_status || 'pending',
    totalStores,
    isCancellationScheduled: isCancellationScheduled(),
    isSuspended: isSuspended(),
  };
};
